import { useModal } from 'contexts/ModalContext'
import { Field, Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import { LuPencil, LuTrash2 } from 'react-icons/lu'
import Select from 'react-select'
import * as Yup from 'yup'

export default function UserFormDetail({
  id,
  isNew,
  user,
  fields,
  roles,
  handleSubmit,
  handleDelete
}) {
  // Hook para gestionar el modal
  const { modal, openModal } = useModal()

  const yesNoValues = [
    { value: 1, label: 'Sí' },
    { value: 0, label: 'No' }
  ]

  // Esquema de validación del formulario (POST o PUT):
  const validationSchema = isNew
    ? Yup.object().shape({
        username: Yup.string()
          .required('Campo obligatorio')
          .min(3, 'Demasiado corto')
          .max(100, 'Demasiado largo'),
        email: Yup.string()
          .email('Correo inválido')
          .required('Introduzca un correo electrónico'),
        first_name: Yup.string()
          .required('Campo obligatorio')
          .min(3, 'Demasiado corto')
          .max(100, 'Demasiado largo'),
        last_name: Yup.string().nullable().max(100, 'Demasiado largo'),
        password: Yup.string()
          .required('Introduzca una contraseña')
          .oneOf(
            [Yup.ref('password_confirmation')],
            'Las contraseñas deben coincidir'
          )
          .min(6, 'La contraseña es demasiado corta. Mínimo 6 caracteres'),
        password_confirmation: Yup.string()
          .required('Introduzca una contraseña')
          .oneOf([Yup.ref('password')], 'Las contraseñas deben coincidir')
          .min(6, 'La contraseña es demasiado corta. Mínimo 6 caracteres')
      })
    : Yup.object().shape({
        username: Yup.string()
          .required('Campo obligatorio')
          .min(3, 'Demasiado corto')
          .max(100, 'Demasiado largo'),
        email: Yup.string()
          .email('Correo inválido')
          .required('Introduzca un correo electrónico'),
        first_name: Yup.string()
          .required('Campo obligatorio')
          .min(3, 'Demasiado corto')
          .max(100, 'Demasiado largo'),
        last_name: Yup.string().nullable().max(100, 'Demasiado largo'),
        password: Yup.string()
          .nullable(true)
          .oneOf(
            [Yup.ref('password_confirmation')],
            'Las contraseñas deben coincidir'
          )
          .min(6, 'La contraseña es demasiado corta. Mínimo 6 caracteres'),
        password_confirmation: Yup.string()
          .nullable(true)
          .oneOf([Yup.ref('password')], 'Las contraseñas deben coincidir')
          .min(6, 'La contraseña es demasiado corta. Mínimo 6 caracteres')
      })

  return (
    <>
      <Formik
        initialValues={{ ...fields }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          handleSubmit(values)
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          setFieldValue,
          submitForm
        }) => (
          <Form className="flex flex-col w-full p-6 mx-auto mt-6 bg-white border border-gray-200 rounded-md dark:border-gray-700 dark:bg-gray-800">
            <div className="grid grid-cols-12 gap-4">
              {/* Usuario */}
              <div className="col-span-12 md:col-span-6">
                <label
                  title="Usuario"
                  className={`${touched.username && errors.username ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="username"
                >
                  Usuario
                </label>

                <Field
                  id="username"
                  name="username"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.username && errors.username ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                  autoComplete="new-password"
                  placeholder={'example@example.com'}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.username && errors.username ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.username || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Correo */}
              <div className="col-span-12 md:col-span-6">
                <label
                  title="Correo"
                  className={`${touched.email && errors.email ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="email"
                >
                  Correo
                </label>

                <Field
                  id="email"
                  name="email"
                  type="email"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.email && errors.email ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                  autoComplete="new-password"
                  placeholder={'example@example.com'}
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.email && errors.email ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.email || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Nombre */}
              <div className="col-span-12 md:col-span-6">
                <label
                  title="Nombre"
                  className={`${touched.first_name && errors.first_name ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="first_name"
                >
                  Nombre
                </label>

                <Field
                  id="first_name"
                  name="first_name"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.first_name && errors.first_name ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                  autoComplete="new-password"
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.first_name && errors.first_name ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.first_name || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Apellidos */}
              <div className="col-span-12 md:col-span-6">
                <label
                  title="Apellidos"
                  className={`${touched.last_name && errors.last_name ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="last_name"
                >
                  Apellidos
                </label>

                <Field
                  id="last_name"
                  name="last_name"
                  type="text"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.last_name && errors.last_name ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                  autoComplete="new-password"
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.last_name && errors.last_name ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.last_name || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Contraseña */}
              <div className="col-span-12 md:col-span-6">
                <label
                  title="Contraseña"
                  className={`${touched.password && errors.password ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="password"
                >
                  Contraseña
                </label>

                <Field
                  id="password"
                  name="password"
                  type="password"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.password && errors.password ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                  autoComplete="new-password"
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.password && errors.password ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.password || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Repetir contraseña */}
              <div className="col-span-12 md:col-span-6">
                <label
                  title="Repetir contraseña"
                  className={`${touched.password_confirmation && errors.password_confirmation ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                  htmlFor="password_confirmation"
                >
                  Repetir contraseña
                </label>

                <Field
                  id="password_confirmation"
                  name="password_confirmation"
                  type="password"
                  className={`block w-full p-2 mt-2 border bg-white rounded-sm focus:ring-opacity-40 focus:outline-none focus:ring ${touched.password_confirmation && errors.password_confirmation ? 'border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 dark:bg-gray-700 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500' : ' text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 focus:border-blue-400 focus:ring-blue-300 dark:focus:border-blue-300'}`}
                  autoComplete="new-password"
                />

                <div
                  className={`flex justify-start items-start w-full ${touched.password_confirmation && errors.password_confirmation ? 'visible' : 'invisible'}`}
                >
                  <div className="flex w-full">
                    <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                      {errors.password_confirmation || '\u00A0'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Rol */}
              {user?.id?.toString() !== id ? (
                <div className="col-span-12 md:col-span-6">
                  <label
                    title="Rol"
                    className={`${touched.role_id && errors.role_id ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                    htmlFor="role_id"
                  >
                    Rol
                  </label>

                  <Select
                    className="w-full mt-2"
                    onChange={(e) => setFieldValue('role_id', e.id)}
                    placeholder="Seleccionar..."
                    options={roles}
                    getOptionLabel={(o) => o.name}
                    getOptionValue={(o) => o.id}
                    isSearchable={false}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '0.125rem',
                        cursor: 'pointer',
                        boxShadow: state.isFocused
                          ? '0 0 0 calc(3px + 0px) rgba(80, 162, 255, 0.3)'
                          : 'none',
                        minHeight: 'auto',
                        lineHeight: '24px',
                        border: state.isFocused
                          ? '1px solid #50A2FF'
                          : '1px solid rgb(229 231 235 / 1)',
                        '&:hover': {
                          border: '1px solid #50A2FF'
                        },
                        outline: '2px solid transparent',
                        outlineOffset: '2px'
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        padding: '0.50rem',
                        position: 'relative',
                        cursor: 'pointer'
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0',
                        paddingTop: '0',
                        paddingBottom: '0',
                        cursor: 'pointer'
                      }),
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        marginLeft: '0',
                        marginRight: '0'
                      }),
                      indicatorsContainer: (baseStyles) => ({
                        ...baseStyles,
                        cursor: 'pointer'
                      }),
                      dropdownIndicator: (baseStyles) => ({
                        ...baseStyles
                      })
                    }}
                    value={roles?.find((role) => {
                      return role.id === values.role_id
                    })}
                  />

                  <div
                    className={`flex justify-start items-start w-full ${touched.role_id && errors.role_id ? 'visible' : 'invisible'}`}
                  >
                    <div className="flex w-full">
                      <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                        {errors.role_id || '\u00A0'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Activo */}
              {user?.id?.toString() !== id ? (
                <div className="col-span-12 md:col-span-6">
                  <label
                    title="Activo"
                    className={`${touched.active && errors.active ? 'text-red-700 dark:text-red-500' : 'text-gray-700 dark:text-gray-200'}`}
                    htmlFor="active"
                  >
                    Activo
                  </label>

                  <Select
                    className="w-full mt-2"
                    onChange={(e) => setFieldValue('is_active', e.value)}
                    placeholder="Seleccionar..."
                    options={yesNoValues}
                    getOptionLabel={(o) => o.label}
                    getOptionValue={(o) => o.value}
                    isSearchable={false}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        borderRadius: '0.125rem',
                        cursor: 'pointer',
                        boxShadow: state.isFocused
                          ? '0 0 0 calc(3px + 0px) rgba(80, 162, 255, 0.3)'
                          : 'none',
                        minHeight: 'auto',
                        lineHeight: '24px',
                        border: state.isFocused
                          ? '1px solid #50A2FF'
                          : '1px solid rgb(229 231 235 / 1)',
                        '&:hover': {
                          border: '1px solid #50A2FF'
                        },
                        outline: '2px solid transparent',
                        outlineOffset: '2px'
                      }),
                      valueContainer: (baseStyles) => ({
                        ...baseStyles,
                        padding: '0.50rem',
                        position: 'relative',
                        cursor: 'pointer'
                      }),
                      input: (baseStyles) => ({
                        ...baseStyles,
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0',
                        paddingTop: '0',
                        paddingBottom: '0',
                        cursor: 'pointer'
                      }),
                      placeholder: (baseStyles) => ({
                        ...baseStyles,
                        marginLeft: '0',
                        marginRight: '0'
                      }),
                      indicatorsContainer: (baseStyles) => ({
                        ...baseStyles,
                        cursor: 'pointer'
                      }),
                      dropdownIndicator: (baseStyles) => ({
                        ...baseStyles
                      })
                    }}
                    value={yesNoValues.find((option) => {
                      return option.value === values.is_active
                    })}
                  />

                  <div
                    className={`flex justify-start items-start w-full ${touched.active && errors.active ? 'visible' : 'invisible'}`}
                  >
                    <div className="flex w-full">
                      <p className="mt-1 text-sm leading-none text-red-600 dark:text-red-500">
                        {errors.active || '\u00A0'}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex justify-end mt-6 gap-x-4">
              {!isNew && user?.id?.toString() !== id ? (
                <button
                  title="Eliminar"
                  type="button"
                  onClick={() =>
                    openModal('DeleteModal', {
                      itemName: fields.username,
                      itemId: fields.id,
                      handleDelete
                    })
                  }
                  className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 bg-red-500 border border-red-500 rounded-sm sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-red-500 dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-red-500 focus:border-red-500 dark:focus:border-red-500"
                >
                  <LuTrash2 size={20} />
                  <span>Eliminar</span>
                </button>
              ) : null}

              <button
                title="Guardar"
                type="submit"
                className="flex items-center justify-center w-1/2 px-5 py-2 space-x-2 text-sm text-white transition-colors duration-200 border rounded-sm bg-accent border-accent sm:w-36 dark:hover:bg-gray-800 enabled:hover:bg-white enabled:hover:text-accent dark:bg-gray-900 dark:text-gray-200 dark:border-gray-700 dark:enabled:hover:bg-gray-800 disabled:opacity-30 focus:ring-opacity-20 focus:outline-none focus:ring focus:ring-accent focus:border-accent dark:focus:border-accent"
              >
                <LuPencil size={20} />
                <span>Guardar</span>
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

UserFormDetail.propTypes = {
  id: PropTypes.any,
  isNew: PropTypes.bool,
  user: PropTypes.object,
  fields: PropTypes.object,
  roles: PropTypes.any,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired
}
